import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { toast } from "sonner";
import { getErrorMessage } from "../utils/errorMessages";
import type { TeamMember } from "../types/team";

export function useRemoveMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (memberId: string) => {
      const { data, error } = await supabase.rpc("remove_member", {
        p_member_id: memberId,
      });

      if (error) {
        throw new Error(error.message);
      }

      return data;
    },
    onMutate: async (memberId: string) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["team-members"] });

      // Snapshot the previous value
      const previousMembers = queryClient.getQueryData<TeamMember[]>([
        "team-members",
      ]);

      // Optimistically remove the member from the UI
      queryClient.setQueryData<TeamMember[]>(["team-members"], (old) => {
        if (!old) return [];
        return old.filter((member) => member.id !== memberId);
      });

      // Return context with previous data for rollback
      return { previousMembers };
    },
    onSuccess: () => {
      toast.success("Member removed");
    },
    onError: (error: Error, _memberId, context) => {
      // Rollback to previous state on error
      if (context?.previousMembers) {
        queryClient.setQueryData(["team-members"], context.previousMembers);
      }
      const message = getErrorMessage(error);
      toast.error(message);
    },
    onSettled: () => {
      // Refetch to ensure sync with server state
      queryClient.invalidateQueries({ queryKey: ["team-members"] });
    },
  });
}
